import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import flatpickr from 'flatpickr';
import { IpemarksService } from '../ipemarks.service';
import { ToastmessageService } from 'src/app/toastmessage.service';
import { NgxSpinnerService } from 'ngx-spinner';

@Component({
  selector: 'app-ipe-test-creation',
  templateUrl: './ipe-test-creation.component.html',
  styleUrls: ['./ipe-test-creation.component.scss']
})
export class IpeTestCreationComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('startDateInput') startDateInput!: ElementRef<HTMLInputElement>;
  @ViewChild('endDateInput') endDateInput!: ElementRef<HTMLInputElement>;

  testForm!: FormGroup;
  private startPicker: any;
  private endPicker: any;

  /** From GET /apiV2/ipeMarkss/states */
  stateList: any[] = [];
  filteredStateList: any[] = [];
  stateQuery = '';
  /** From GET /apiV2/ipeMarkss/academicYears */
  academicYearList: any[] = [];
  filteredAcademicYearList: any[] = [];
  academicYearQuery = '';
  /** From GET /apiV2/ipeMarkss/classes */
  classList: any[] = [];
  filteredClassList: any[] = [];
  classQuery = '';
  /** From GET /apiV2/ipeMarkss/groups?classId= */
  groupList: any[] = [];
  filteredGroupList: any[] = [];
  groupQuery = '';
  /** From GET /apiV2/ipeMarkss/streams?classId=&groupName= */
  streamList: any[] = [];
  filteredStreamList: any[] = [];
  streamQuery = '';
  /** From GET /apiV2/ipeMarkss/programNames?classId=&groupName=&streamId= */
  programList: any[] = [];
  filteredProgramList: any[] = [];
  programQuery = '';
  /** From GET /apiV2/ipeMarkss/testTypes */
  testTypeList: any[] = [];
  filteredTestTypeList: any[] = [];
  testTypeQuery = '';

  /** From GET /apiV2/ipeMarkss/testSubjects?groupName=&testType=&stateId= */
  subjects: Array<{
    key: string;
    label: string;
    /** Exact subjectName expected by createTest API (e.g. MATHS A) */
    apiName: string;
    maxMarks: number | null;
    selected: boolean;
    subjectId?: any;
  }> = [];
  isCreating = false;
  /** Shown when createTest API returns duplicate / validation error */
  createTestError = '';
  existingTestCode = '';
  existingTestName = '';
  /** Only this field shows error/focus at a time (one-by-one validation) */
  activeErrorField: string | null = null;

  showCreateConfirm = false;
  confirmRows: Array<{ label: string; value: string }> = [];
  confirmSubjectLabels: string[] = [];

  constructor(
    private fb: FormBuilder,
    private services: IpemarksService,
    private toast: ToastmessageService,
    private spinner: NgxSpinnerService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone
  ) {}

  ngOnInit(): void {
    this.buildForm();
    this.loadStates();
    this.loadTestTypes();
  }

  ngAfterViewInit(): void {
    // Delay so ViewChild inputs are ready before Flatpickr binds
    setTimeout(() => this.initDatePickers(), 0);
  }

  ngOnDestroy(): void {
    this.destroyDatePickers();
  }

  private buildForm(): void {
    this.subjects = [];
    this.testForm = this.fb.group({
      stateName: ['', Validators.required],
      stateId: [''],
      academicYear: ['', Validators.required],
      academicYearId: [''],
      className: ['', Validators.required],
      classId: [''],
      groupName: ['', Validators.required],
      groupId: [''],
      streamName: ['', Validators.required],
      streamId: [''],
      programName: ['', Validators.required],
      programId: [''],
      testType: ['', Validators.required],
      testTypeId: [''],
      testTypeCode: [''],
      testTypeNumber: [1],
      testStartDate: ['', Validators.required],
      testEndDate: ['', Validators.required]
    });
  }

  /** Load Test Type dropdown (independent of class chain) */
  loadTestTypes(): void {
    this.services.getTestTypes().subscribe({
      next: (res: any) => {
        this.testTypeList = this.normalizeList(res, ['testTypes', 'testTypeList', 'types', 'data']);
        this.testTypeQuery = '';
        this.filteredTestTypeList = [...this.testTypeList];
        // Do not auto-select — wait for user choice
        this.testForm.patchValue({ testType: '', testTypeId: '', testTypeCode: '' });
        this.applySubjects([]);
      },
      error: (err) => {
        console.error('Failed to load test types', err);
        this.testTypeList = [];
        this.filteredTestTypeList = [];
        this.testForm.patchValue({ testType: '', testTypeId: '', testTypeCode: '' });
        this.applySubjects([]);
        this.toast.showError('Failed to load Test Type list');
      }
    });
  }

  /** Load Subject max marks for selected groupName + testType + stateId */
  loadSubjects(): void {
    const groupName = this.testForm?.value?.groupName;
    const testType =
      this.testForm?.value?.testTypeCode ||
      this.toApiTestType(this.testForm?.value?.testType);
    const stateId = this.testForm?.value?.stateId;

    if (!groupName || !testType || stateId == null || String(stateId).trim() === '') {
      this.applySubjects([]);
      return;
    }

    this.services.getTestSubjects(groupName, testType, stateId).subscribe({
      next: (res: any) => {
        const list = this.normalizeList(res, [
          'testSubjects',
          'subjects',
          'subjectList',
          'data'
        ]);
        this.applySubjects(list);
      },
      error: (err) => {
        console.error('Failed to load subjects', err);
        this.applySubjects([]);
        this.toast.showError('Failed to load Subject list');
      }
    });
  }

  private applySubjects(apiList: any[]): void {
    (this.subjects || []).forEach((s) => {
      if (this.testForm?.contains(s.key)) {
        this.testForm.removeControl(s.key);
      }
    });

    this.subjects = (apiList || []).map((item, index) => {
      const label = (
        item?.subjectName ||
        item?.name ||
        item?.subject ||
        item?.displayName ||
        item?.label ||
        `Subject ${index + 1}`
      ).toString();
      const subjectId = item?.subjectCode || item?.subjectId || item?.id || index;
      const key = 'sub_' + String(subjectId).replace(/\s+/g, '_');
      return {
        key,
        label,
        apiName: this.toApiSubjectName(item, label),
        maxMarks: null as number | null,
        selected: false,
        subjectId
      };
    });

    this.subjects.forEach((s) => {
      this.testForm.addControl(
        s.key,
        this.fb.control({ value: '', disabled: true }, [Validators.min(1), Validators.max(100)])
      );
    });
  }

  /** Map UI / subjectCode names → createTest API subjectName (e.g. Mathematics A → MATHS A) */
  private toApiSubjectName(item: any, label: string): string {
    const raw = (
      item?.subjectCode ||
      item?.code ||
      item?.apiSubjectName ||
      item?.subject ||
      label ||
      ''
    ).toString().trim();
    return this.normalizeApiSubjectName(raw);
  }

  private normalizeApiSubjectName(raw: string): string {
    const n = (raw || '')
      .toString()
      .trim()
      .toUpperCase()
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ');

    if (!n) {
      return '';
    }

    // Maths variants — backend expects "MATHS A" / "MATHS B"
    if (
      n === 'MATHS A' ||
      n === 'MATH A' ||
      n === 'MATHA' ||
      n === 'MATHEMATICS A' ||
      n === 'MATHEMATIC A'
    ) {
      return 'MATHS A';
    }
    if (
      n === 'MATHS B' ||
      n === 'MATH B' ||
      n === 'MATHB' ||
      n === 'MATHEMATICS B' ||
      n === 'MATHEMATIC B'
    ) {
      return 'MATHS B';
    }

    if (n === 'ENG' || n === 'ENGLISH') {
      return 'ENGLISH';
    }
    if (
      n === 'SECOND LANGUAGE' ||
      n === 'SECOND LANG' ||
      n === 'SECLANG' ||
      n === 'SEC LANG' ||
      n === 'SL' ||
      n.includes('SECOND')
    ) {
      return 'SECOND LANGUAGE';
    }
    if (n === 'PHY' || n === 'PHYSICS') {
      return 'PHYSICS';
    }
    if (n === 'CHE' || n === 'CHEM' || n === 'CHEMISTRY') {
      return 'CHEMISTRY';
    }
    if (n === 'BOT' || n === 'BOTANY') {
      return 'BOTANY';
    }
    if (n === 'ZOO' || n === 'ZOOLOGY') {
      return 'ZOOLOGY';
    }
    if (n === 'ECO' || n === 'ECONOMICS') {
      return 'ECONOMICS';
    }
    if (n === 'COM' || n === 'COMMERCE') {
      return 'COMMERCE';
    }

    return n;
  }

  private toApiTestType(value: string): string {
    return (value || '').toString().trim().replace(/\s+/g, '_');
  }

  /** Convert flatpickr d-M-Y (25-Jul-2026) → yyyy-MM-dd */
  private toApiDate(value: string): string {
    const raw = (value || '').toString().trim();
    if (!raw) {
      return '';
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      return raw;
    }
    const parsed = flatpickr.parseDate(raw, 'd-M-Y');
    if (!parsed) {
      return raw;
    }
    const y = parsed.getFullYear();
    const m = String(parsed.getMonth() + 1).padStart(2, '0');
    const d = String(parsed.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  private toNumber(value: any): number {
    const n = Number(value);
    return isNaN(n) ? 0 : n;
  }

  private getSelectedSubjects() {
    return (this.subjects || []).filter((s) => s.selected === true);
  }

  private buildCreatePayload(): any {
    const v = this.testForm.value;
    const subjects = this.getSelectedSubjects().map((s) => {
      const raw = this.testForm.get(s.key)?.value;
      const maxMarks = this.toNumber(raw);
      return {
        subjectName: s.apiName || this.normalizeApiSubjectName(s.label) || s.label || '',
        marks: String(raw ?? ''),
        maxMarks
      };
    });

    return {
      academicId: this.toNumber(v.academicYearId),
      classId: this.toNumber(v.classId),
      className: v.className || '',
      groupId: this.toNumber(v.groupId),
      groupName: v.groupName || '',
      streamId: this.toNumber(v.streamId),
      streamName: v.streamName || '',
      programId: this.toNumber(v.programId),
      programName: v.programName || '',
      stateId: this.toNumber(v.stateId),
      stateName: v.stateName || '',
      testType: v.testTypeCode || this.toApiTestType(v.testType),
      testStartDate: this.toApiDate(v.testStartDate),
      testEndDate: this.toApiDate(v.testEndDate),
      subjects,
      testTotalMarks: this.totalMarks
    };
  }

  /** Load State dropdown (before Academic Year) */
  loadStates(): void {
    this.spinner.show();
    this.services.getIpeStates().subscribe({
      next: (res: any) => {
        this.stateList = this.normalizeList(res, ['states', 'stateList', 'data']);
        this.stateQuery = '';
        this.filteredStateList = [...this.stateList];
        this.testForm.patchValue({ stateName: '', stateId: '', academicYear: '', academicYearId: '' });
        this.academicYearList = [];
        this.filteredAcademicYearList = [];
        this.classList = [];
        this.filteredClassList = [];
        this.clearDependentLists(['group', 'stream', 'program']);
        this.applySubjects([]);
        this.spinner.hide();
      },
      error: (err) => {
        console.error('Failed to load states', err);
        this.stateList = [];
        this.filteredStateList = [];
        this.toast.showError('Failed to load State list');
        this.spinner.hide();
      }
    });
  }

  /** Load Academic Year dropdown */
  loadAcademicYears(stateId?: any): void {
    const id = stateId ?? this.testForm?.value?.stateId;
    if (id === null || id === undefined || String(id).trim() === '') {
      this.academicYearList = [];
      this.filteredAcademicYearList = [];
      this.testForm.patchValue({ academicYear: '', academicYearId: '' });
      this.classList = [];
      this.filteredClassList = [];
      this.clearDependentLists(['group', 'stream', 'program']);
      this.applySubjects([]);
      return;
    }
    this.spinner.show();
    this.services.getAcademicYears(id).subscribe({
      next: (res: any) => {
        this.academicYearList = this.normalizeAcademicYearList(res);
        this.academicYearQuery = '';
        this.filteredAcademicYearList = [...this.academicYearList];
        this.testForm.patchValue({ academicYear: '', academicYearId: '' });
        this.classList = [];
        this.filteredClassList = [];
        this.clearDependentLists(['group', 'stream', 'program']);
        this.applySubjects([]);
        this.spinner.hide();
      },
      error: (err) => {
        console.error('Failed to load academic years', err);
        this.academicYearList = [];
        this.filteredAcademicYearList = [];
        this.toast.showError('Failed to load Academic Year list');
        this.spinner.hide();
      }
    });
  }

  /** Load Class dropdown from testCreationbyClasses (returns academicId per class) */
  loadClasses(academicYear?: string): void {
    const year = academicYear || this.testForm?.value?.academicYear;
    if (!year) {
      this.classList = [];
      this.filteredClassList = [];
      this.testForm.patchValue({ className: '', classId: '', academicYearId: '' });
      this.clearDependentLists(['group', 'stream', 'program']);
      this.applySubjects([]);
      return;
    }

    this.spinner.show();
    this.services.getClassestest(year).subscribe({
      next: (res: any) => {
        this.classList = this.normalizeList(res, ['classes', 'classList', 'data']);
        this.classQuery = '';
        this.filteredClassList = [...this.classList];
        // Do not auto-select — user must choose Class
        this.testForm.patchValue({ className: '', classId: '', academicYearId: '' });
        this.clearDependentLists(['group', 'stream', 'program']);
        this.applySubjects([]);
        this.spinner.hide();
      },
      error: (err) => {
        console.error('Failed to load classes', err);
        this.classList = [];
        this.filteredClassList = [];
        this.toast.showError('Failed to load Class list');
        this.spinner.hide();
      }
    });
  }

  /** Load Group dropdown for selected academicYear + classId */
  loadGroups(classId: any): void {
    const academicYear = this.testForm?.value?.academicYear;
    if (!academicYear || (!classId && classId !== 0)) {
      this.clearDependentLists(['group', 'stream', 'program']);
      this.applySubjects([]);
      this.spinner.hide();
      return;
    }

    this.spinner.show();
    this.services.getGroupstest(academicYear, classId).subscribe({
      next: (res: any) => {
        this.groupList = this.normalizeList(res, ['groups', 'groupList', 'data']);
        this.groupQuery = '';
        this.filteredGroupList = [...this.groupList];
        this.testForm.patchValue({
          groupName: '',
          groupId: '',
          streamName: '',
          streamId: '',
          programName: '',
          programId: ''
        });
        this.streamList = [];
        this.filteredStreamList = [];
        this.programList = [];
        this.filteredProgramList = [];
        this.applySubjects([]);
        this.spinner.hide();
      },
      error: (err) => {
        console.error('Failed to load groups', err);
        this.clearDependentLists(['group', 'stream', 'program']);
        this.applySubjects([]);
        this.toast.showError('Failed to load Group list');
        this.spinner.hide();
      }
    });
  }

  /** Load Stream dropdown for academicYear + classId + groupId */
  loadStreams(classId: any, groupId: any): void {
    const academicYear = this.testForm?.value?.academicYear;
    if (!academicYear || (!classId && classId !== 0) || (!groupId && groupId !== 0)) {
      this.clearDependentLists(['stream', 'program']);
      this.spinner.hide();
      return;
    }

    this.spinner.show();
    this.services.getStreamstest(academicYear, classId, groupId).subscribe({
      next: (res: any) => {
        this.streamList = this.normalizeList(res, ['streams', 'streamList', 'data']);
        this.streamQuery = '';
        this.filteredStreamList = [...this.streamList];
        this.testForm.patchValue({
          streamName: '',
          streamId: '',
          programName: '',
          programId: ''
        });
        this.programList = [];
        this.filteredProgramList = [];
        this.spinner.hide();
      },
      error: (err) => {
        console.error('Failed to load streams', err);
        this.clearDependentLists(['stream', 'program']);
        this.toast.showError('Failed to load Stream list');
        this.spinner.hide();
      }
    });
  }

  /** Load Program dropdown for academicYear + classId + groupId + streamId + stateId */
  loadPrograms(classId: any, streamId: any): void {
    const academicYear = this.testForm?.value?.academicYear;
    const groupId = this.testForm?.value?.groupId;
    const stateId = this.testForm?.value?.stateId;
    if (
      !academicYear ||
      (!classId && classId !== 0) ||
      (!streamId && streamId !== 0) ||
      (!groupId && groupId !== 0) ||
      stateId == null ||
      String(stateId).trim() === ''
    ) {
      this.clearDependentLists(['program']);
      this.spinner.hide();
      return;
    }

    this.spinner.show();
    this.services.getProgramNamestest(academicYear, classId, groupId, streamId, stateId).subscribe({
      next: (res: any) => {
        this.programList = this.normalizeList(res, ['programNames', 'programs', 'programList', 'data']);
        this.programQuery = '';
        this.filteredProgramList = [...this.programList];
        this.testForm.patchValue({ programName: '', programId: '' });
        this.spinner.hide();
      },
      error: (err) => {
        console.error('Failed to load programs', err);
        this.clearDependentLists(['program']);
        this.toast.showError('Failed to load Program list');
        this.spinner.hide();
      }
    });
  }

  private clearDependentLists(levels: Array<'group' | 'stream' | 'program'>): void {
    const patch: any = {};
    if (levels.includes('group')) {
      this.groupList = [];
      this.filteredGroupList = [];
      this.groupQuery = '';
      patch.groupName = '';
      patch.groupId = '';
    }
    if (levels.includes('stream')) {
      this.streamList = [];
      this.filteredStreamList = [];
      this.streamQuery = '';
      patch.streamName = '';
      patch.streamId = '';
    }
    if (levels.includes('program')) {
      this.programList = [];
      this.filteredProgramList = [];
      this.programQuery = '';
      patch.programName = '';
      patch.programId = '';
    }
    this.testForm.patchValue(patch);
  }

  private normalizeList(res: any, keys: string[]): any[] {
    if (!res) {
      return [];
    }
    if (Array.isArray(res)) {
      return res;
    }
    for (const key of keys) {
      if (Array.isArray(res[key])) {
        return res[key];
      }
    }
    return [];
  }

  /** currentAcademicYear returns a single object */
  private normalizeAcademicYearList(res: any): any[] {
    if (!res) {
      return [];
    }
    if (Array.isArray(res)) {
      return res;
    }
    if (res.academicYear || res.academicId) {
      return [res];
    }
    return this.normalizeList(res, ['academicYears', 'academicYearList', 'years', 'data']);
  }

  getStateLabel(item: any): string {
    if (item == null) {
      return '';
    }
    if (typeof item === 'string' || typeof item === 'number') {
      return String(item);
    }
    return (
      item.stateName ||
      item.name ||
      item.displayName ||
      item.label ||
      item.state ||
      ''
    ).toString();
  }

  getStateId(item: any): any {
    if (item == null || typeof item === 'string' || typeof item === 'number') {
      return item ?? '';
    }
    return item.stateId ?? item.id ?? item.stateCode ?? '';
  }

  getAcademicYearLabel(item: any): string {
    if (item == null) {
      return '';
    }
    if (typeof item === 'string' || typeof item === 'number') {
      return String(item);
    }
    return (
      item.academicYear ||
      item.academicYearName ||
      item.year ||
      item.name ||
      item.displayName ||
      item.label ||
      ''
    ).toString();
  }

  getAcademicYearId(item: any): any {
    if (item == null || typeof item === 'string' || typeof item === 'number') {
      return item ?? '';
    }
    return item.academicId ?? item.academicYearId ?? item.id ?? item.yearId ?? '';
  }

  getClassLabel(item: any): string {
    if (item == null) {
      return '';
    }
    if (typeof item === 'string' || typeof item === 'number') {
      return String(item);
    }
    return (
      item.className ||
      item.name ||
      item.displayName ||
      item.class ||
      item.label ||
      ''
    ).toString();
  }

  getClassId(item: any): any {
    if (item == null || typeof item === 'string' || typeof item === 'number') {
      return item ?? '';
    }
    return item.classId ?? item.id ?? item.classCode ?? '';
  }

  /** Per-class academicId from testCreationbyClasses (Jr/Sr can differ). */
  getClassAcademicId(item: any): any {
    if (item == null || typeof item === 'string' || typeof item === 'number') {
      return item ?? '';
    }
    return item.academicId ?? item.academicYearId ?? '';
  }

  getGroupLabel(item: any): string {
    if (item == null) {
      return '';
    }
    if (typeof item === 'string' || typeof item === 'number') {
      return String(item);
    }
    return (
      item.groupName ||
      item.name ||
      item.displayName ||
      item.group ||
      item.label ||
      ''
    ).toString();
  }

  getGroupId(item: any): any {
    if (item == null || typeof item === 'string' || typeof item === 'number') {
      return item ?? '';
    }
    return item.groupId ?? item.id ?? item.groupCode ?? '';
  }

  getStreamLabel(item: any): string {
    if (item == null) {
      return '';
    }
    if (typeof item === 'string' || typeof item === 'number') {
      return String(item);
    }
    return (
      item.streamName ||
      item.name ||
      item.displayName ||
      item.stream ||
      item.label ||
      ''
    ).toString();
  }

  getStreamId(item: any): any {
    if (item == null || typeof item === 'string' || typeof item === 'number') {
      return item ?? '';
    }
    return item.streamId ?? item.id ?? item.streamCode ?? '';
  }

  getProgramLabel(item: any): string {
    if (item == null) {
      return '';
    }
    if (typeof item === 'string' || typeof item === 'number') {
      return String(item);
    }
    return (
      item.programName ||
      item.name ||
      item.displayName ||
      item.program ||
      item.label ||
      ''
    ).toString();
  }

  getProgramId(item: any): any {
    if (item == null || typeof item === 'string' || typeof item === 'number') {
      return item ?? '';
    }
    return item.programId ?? item.id ?? item.programCode ?? '';
  }

  getTestTypeLabel(item: any): string {
    if (item == null) {
      return '';
    }
    if (typeof item === 'string' || typeof item === 'number') {
      return String(item);
    }
    return String(
      item.testType ||
      item.testTypeCode ||
      item.testTypeName ||
      item.name ||
      item.displayName ||
      item.label ||
      item.type ||
      ''
    );
  }

  getTestTypeId(item: any): any {
    if (item == null || typeof item === 'string' || typeof item === 'number') {
      return item ?? '';
    }
    return item.id ?? item.testTypeId ?? item.typeId ?? '';
  }

  /** Value sent to testSubjects API (e.g. UNIT_TEST / WEEK_END) */
  getTestTypeApiValue(item: any): string {
    if (item == null) {
      return '';
    }
    if (typeof item === 'string' || typeof item === 'number') {
      return this.toApiTestType(String(item));
    }
    const code =
      item.testType ||
      item.testTypeCode ||
      item.code ||
      item.type ||
      '';
    return this.toApiTestType(String(code || this.getTestTypeLabel(item)));
  }

  private matchesQuery(label: string, query: string): boolean {
    return (label || '').toLowerCase().includes((query || '').toLowerCase().trim());
  }

  filterStates(): void {
    this.filteredStateList = (this.stateList || []).filter((item) =>
      this.matchesQuery(this.getStateLabel(item), this.stateQuery)
    );
  }

  filterAcademicYears(): void {
    this.filteredAcademicYearList = (this.academicYearList || []).filter((item) =>
      this.matchesQuery(this.getAcademicYearLabel(item), this.academicYearQuery)
    );
  }

  filterClasses(): void {
    this.filteredClassList = (this.classList || []).filter((item) =>
      this.matchesQuery(this.getClassLabel(item), this.classQuery)
    );
  }

  filterGroups(): void {
    this.filteredGroupList = (this.groupList || []).filter((item) =>
      this.matchesQuery(this.getGroupLabel(item), this.groupQuery)
    );
  }

  filterStreams(): void {
    this.filteredStreamList = (this.streamList || []).filter((item) =>
      this.matchesQuery(this.getStreamLabel(item), this.streamQuery)
    );
  }

  filterPrograms(): void {
    this.filteredProgramList = (this.programList || []).filter((item) =>
      this.matchesQuery(this.getProgramLabel(item), this.programQuery)
    );
  }

  filterTestTypes(): void {
    this.filteredTestTypeList = (this.testTypeList || []).filter((item) =>
      this.matchesQuery(this.getTestTypeLabel(item), this.testTypeQuery)
    );
  }

  selectState(item: any): void {
    const stateId = this.getStateId(item);
    this.testForm.patchValue({
      stateName: this.getStateLabel(item),
      stateId,
      academicYear: '',
      academicYearId: '',
      className: '',
      classId: ''
    });
    this.stateQuery = '';
    this.filteredStateList = [...this.stateList];
    if (this.activeErrorField === 'stateName') {
      this.clearActiveError();
    }
    this.classList = [];
    this.filteredClassList = [];
    this.clearDependentLists(['group', 'stream', 'program']);
    this.applySubjects([]);
    this.loadAcademicYears(stateId);
  }

  selectAcademicYear(item: any): void {
    const academicYear = this.getAcademicYearLabel(item);
    this.testForm.patchValue({
      academicYear,
      academicYearId: '',
      className: '',
      classId: ''
    });
    this.academicYearQuery = '';
    this.filteredAcademicYearList = [...this.academicYearList];
    if (this.activeErrorField === 'academicYear') {
      this.clearActiveError();
    }
    this.clearDependentLists(['group', 'stream', 'program']);
    this.applySubjects([]);
    this.loadClasses(academicYear);
  }

  selectClass(item: any): void {
    const classId = this.getClassId(item);
    this.testForm.patchValue({
      className: this.getClassLabel(item),
      classId,
      academicYearId: this.getClassAcademicId(item)
    });
    this.classQuery = '';
    this.filteredClassList = [...this.classList];
    if (this.activeErrorField === 'className') {
      this.clearActiveError();
    }
    this.clearDependentLists(['group', 'stream', 'program']);
    this.loadGroups(classId);
  }

  selectGroup(item: any): void {
    const groupName = this.getGroupLabel(item);
    const groupId = this.getGroupId(item);
    const classId = this.testForm.value.classId;
    this.testForm.patchValue({
      groupName,
      groupId
    });
    this.groupQuery = '';
    this.filteredGroupList = [...this.groupList];
    if (this.activeErrorField === 'groupName') {
      this.clearActiveError();
    }
    this.clearDependentLists(['stream', 'program']);
    this.loadSubjects();
    this.loadStreams(classId, groupId);
  }

  selectStream(item: any): void {
    const streamId = this.getStreamId(item);
    const classId = this.testForm.value.classId;
    this.testForm.patchValue({
      streamName: this.getStreamLabel(item),
      streamId
    });
    this.streamQuery = '';
    this.filteredStreamList = [...this.streamList];
    if (this.activeErrorField === 'streamName') {
      this.clearActiveError();
    }
    this.clearDependentLists(['program']);
    this.loadPrograms(classId, streamId);
  }

  selectProgram(item: any): void {
    this.testForm.patchValue({
      programName: this.getProgramLabel(item),
      programId: this.getProgramId(item)
    });
    this.programQuery = '';
    this.filteredProgramList = [...this.programList];
    if (this.activeErrorField === 'programName') {
      this.clearActiveError();
    }
  }

  selectTestType(item: any): void {
    this.testForm.patchValue({
      testType: this.getTestTypeLabel(item),
      testTypeId: this.getTestTypeId(item),
      testTypeCode: this.getTestTypeApiValue(item)
    });
    this.testTypeQuery = '';
    this.filteredTestTypeList = [...this.testTypeList];
    if (this.activeErrorField === 'testType') {
      this.clearActiveError();
    }
    this.loadSubjects();
  }

  private initDatePickers(): void {
    this.destroyDatePickers();

    if (this.startDateInput?.nativeElement) {
      this.startPicker = flatpickr(this.startDateInput.nativeElement, {
        dateFormat: 'd-M-Y',
        allowInput: false,
        clickOpens: true,
        closeOnSelect: true,
        disableMobile: true,
        defaultDate: this.testForm.value.testStartDate || undefined,
        appendTo: document.body,
        static: false,
        onReady: (_dates, _str, instance) => {
          instance.calendarContainer.classList.add('ipe-create-flatpickr');
        },
        onChange: (_dates, dateStr) => {
          this.ngZone.run(() => {
            this.testForm.get('testStartDate')?.setValue(dateStr || '', { emitEvent: false });
            this.testForm.get('testStartDate')?.markAsDirty();
            this.testForm.get('testStartDate')?.markAsTouched();
            if (this.activeErrorField === 'testStartDate') {
              this.clearActiveError();
            }
            if (this.endPicker) {
              this.endPicker.set('minDate', dateStr || undefined);
              const endVal = (this.testForm.value.testEndDate || '').toString();
              if (dateStr && endVal) {
                const start = flatpickr.parseDate(dateStr, 'd-M-Y');
                const end = flatpickr.parseDate(endVal, 'd-M-Y');
                if (start && end && end < start) {
                  this.endPicker.clear();
                  this.testForm.get('testEndDate')?.setValue('', { emitEvent: false });
                }
              }
            }
            this.cdr.detectChanges();
            setTimeout(() => this.startPicker?.close(), 0);
          });
        }
      });
    }

    if (this.endDateInput?.nativeElement) {
      this.endPicker = flatpickr(this.endDateInput.nativeElement, {
        dateFormat: 'd-M-Y',
        allowInput: false,
        clickOpens: true,
        closeOnSelect: true,
        disableMobile: true,
        defaultDate: this.testForm.value.testEndDate || undefined,
        minDate: this.testForm.value.testStartDate || undefined,
        appendTo: document.body,
        static: false,
        onReady: (_dates, _str, instance) => {
          instance.calendarContainer.classList.add('ipe-create-flatpickr');
        },
        onChange: (_dates, dateStr) => {
          this.ngZone.run(() => {
            this.testForm.get('testEndDate')?.setValue(dateStr || '', { emitEvent: false });
            this.testForm.get('testEndDate')?.markAsDirty();
            this.testForm.get('testEndDate')?.markAsTouched();
            if (this.activeErrorField === 'testEndDate') {
              this.clearActiveError();
            }
            this.cdr.detectChanges();
            setTimeout(() => this.endPicker?.close(), 0);
          });
        }
      });
    }
  }

  private destroyDatePickers(): void {
    if (this.startPicker) {
      this.startPicker.destroy();
      this.startPicker = null;
    }
    if (this.endPicker) {
      this.endPicker.destroy();
      this.endPicker = null;
    }
  }

  openStartCalendar(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (!this.startPicker && this.startDateInput?.nativeElement) {
      this.initDatePickers();
    }
    setTimeout(() => this.startPicker?.open(), 0);
  }

  openEndCalendar(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (!this.endPicker && this.endDateInput?.nativeElement) {
      this.initDatePickers();
    }
    setTimeout(() => this.endPicker?.open(), 0);
  }

  get totalMarks(): number {
    if (!this.testForm) {
      return 0;
    }
    return this.getSelectedSubjects().reduce((sum, s) => {
      const val = Number(this.testForm.get(s.key)?.value);
      return sum + (isNaN(val) ? 0 : val);
    }, 0);
  }

  get leftSubjects() {
    const mid = Math.ceil(this.subjects.length / 2);
    return this.subjects.slice(0, mid);
  }

  get rightSubjects() {
    const mid = Math.ceil(this.subjects.length / 2);
    return this.subjects.slice(mid);
  }

  private readonly requiredFieldOrder: Array<{ key: string; message: string }> = [
    { key: 'stateName', message: 'Please select State' },
    { key: 'academicYear', message: 'Please select Academic Year' },
    { key: 'className', message: 'Please select Class' },
    { key: 'groupName', message: 'Please select Group' },
    { key: 'streamName', message: 'Please select Stream' },
    { key: 'programName', message: 'Please select Program' },
    { key: 'testType', message: 'Please select Test Type' },
    { key: 'testStartDate', message: 'Please select Test Start Date' },
    { key: 'testEndDate', message: 'Please select Test End Date' }
  ];

  /** Show error only for the single active field (one-by-one) */
  isFieldInvalid(fieldKey: string): boolean {
    return this.activeErrorField === fieldKey;
  }

  isSubjectInvalid(subject: { key: string }): boolean {
    return this.isFieldInvalid(subject.key);
  }

  getSubjectError(subject: { key: string; label: string }): string {
    if (this.activeErrorField !== subject.key) {
      return '';
    }
    const ctrl = this.testForm?.get(subject.key);
    if (!ctrl || !ctrl.errors) {
      return `Please enter ${subject.label} max marks`;
    }
    if (ctrl.errors['required']) {
      return `Please enter ${subject.label} max marks`;
    }
    if (ctrl.errors['min']) {
      return `${subject.label} marks must be greater than 0`;
    }
    if (ctrl.errors['max']) {
      return `${subject.label} max marks cannot exceed 100`;
    }
    return `Please enter valid ${subject.label} max marks`;
  }

  private getFirstMissingStep(): { key: string; message: string } | null {
    for (const field of this.requiredFieldOrder) {
      const value = this.testForm.get(field.key)?.value;
      if (value === null || value === undefined || String(value).trim() === '') {
        return field;
      }
    }

    if (!this.subjects.length) {
      if (!this.testForm.value.groupName) {
        return { key: 'groupName', message: 'Please select Group' };
      }
      if (!this.testForm.value.testType) {
        return { key: 'testType', message: 'Please select Test Type' };
      }
      return {
        key: 'groupName',
        message: 'No subjects found for selected Group and Test Type'
      };
    }

    const selectedSubjects = this.getSelectedSubjects();
    if (!selectedSubjects.length) {
      return {
        key: this.subjects[0].key,
        message: 'Please select at least one subject'
      };
    }

    for (const subject of selectedSubjects) {
      const ctrl = this.testForm.get(subject.key);
      const value = ctrl?.value;
      if (value === null || value === undefined || String(value).trim() === '') {
        return {
          key: subject.key,
          message: `Please enter ${subject.label} max marks`
        };
      }
      const num = Number(value);
      if (isNaN(num) || num <= 0) {
        return {
          key: subject.key,
          message: `${subject.label} marks must be greater than 0`
        };
      }
      if (num > 100) {
        return {
          key: subject.key,
          message: `${subject.label} max marks cannot exceed 100`
        };
      }
      if (ctrl?.invalid) {
        const msg = ctrl.errors?.['max']
          ? `${subject.label} max marks cannot exceed 100`
          : ctrl.errors?.['min']
            ? `${subject.label} marks must be greater than 0`
            : `Please enter valid ${subject.label} max marks`;
        return { key: subject.key, message: msg };
      }
    }

    return null;
  }

  private clearActiveError(): void {
    this.activeErrorField = null;
  }

  get allSubjectsSelected(): boolean {
    return !!this.subjects?.length && this.subjects.every((s) => s.selected);
  }

  get someSubjectsSelected(): boolean {
    return !!this.subjects?.length && !this.allSubjectsSelected && this.subjects.some((s) => s.selected);
  }

  onSelectAllSubjects(event: Event): void {
    const checked = !!(event.target as HTMLInputElement)?.checked;
    (this.subjects || []).forEach((subject) => this.applySubjectSelection(subject, checked));
  }

  onSubjectSelect(subjectKey: string, event: Event): void {
    const checked = !!(event.target as HTMLInputElement)?.checked;
    const subject = (this.subjects || []).find((s) => s.key === subjectKey);
    if (!subject) {
      return;
    }
    this.applySubjectSelection(subject, checked);
  }

  private applySubjectSelection(subject: { key: string; selected: boolean }, checked: boolean): void {
    subject.selected = checked;
    const ctrl = this.testForm.get(subject.key);
    if (!ctrl) {
      return;
    }
    if (checked) {
      ctrl.enable({ emitEvent: false });
      ctrl.setValidators([Validators.required, Validators.min(1), Validators.max(100)]);
    } else {
      ctrl.setValue('', { emitEvent: false });
      ctrl.clearValidators();
      ctrl.disable({ emitEvent: false });
      if (this.activeErrorField === subject.key) {
        this.clearActiveError();
      }
    }
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  onSubjectMarksInput(fieldKey: string): void {
    const ctrl = this.testForm.get(fieldKey);
    const raw = ctrl?.value;
    if (raw !== null && raw !== undefined && String(raw).trim() !== '') {
      const num = Number(raw);
      if (!isNaN(num) && num > 100) {
        ctrl?.setValue(100, { emitEvent: false });
        this.toast.showWarning('Max marks cannot exceed 100');
      }
    }
    if (this.activeErrorField === fieldKey) {
      this.clearActiveError();
    }
  }

  private showOneByOneError(step: { key: string; message: string }): void {
    // Mark only this control so validation stays one-by-one
    Object.keys(this.testForm.controls).forEach((key) => {
      const ctrl = this.testForm.get(key);
      if (!ctrl) {
        return;
      }
      if (key === step.key) {
        ctrl.markAsTouched();
        ctrl.markAsDirty();
      } else {
        ctrl.markAsUntouched();
        ctrl.markAsPristine();
      }
    });

    this.activeErrorField = step.key;
    this.toast.showError(step.message);
    setTimeout(() => this.focusField(step.key), 0);
  }

  private focusField(fieldKey: string): void {
    const isSubject = (this.subjects || []).some((s) => s.key === fieldKey);
    const selector = isSubject
      ? `[data-subject-key="${fieldKey}"]`
      : `[data-field="${fieldKey}"]`;
    const el = document.querySelector(selector) as HTMLElement | null;
    if (!el) {
      return;
    }
    el.focus();
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });

    if (fieldKey === 'testStartDate') {
      this.openStartCalendar();
    } else if (fieldKey === 'testEndDate') {
      this.openEndCalendar();
    } else if (!isSubject && typeof (window as any).bootstrap !== 'undefined') {
      try {
        const Dropdown = (window as any).bootstrap?.Dropdown;
        if (Dropdown) {
          Dropdown.getOrCreateInstance(el).show();
        }
      } catch {
        // ignore if bootstrap dropdown API unavailable
      }
    }
  }

  onReset(): void {
    this.createTestError = '';
    this.existingTestCode = '';
    this.existingTestName = '';
    this.clearActiveError();
    this.buildForm();
    this.groupList = [];
    this.filteredGroupList = [];
    this.groupQuery = '';
    this.streamList = [];
    this.filteredStreamList = [];
    this.streamQuery = '';
    this.programList = [];
    this.filteredProgramList = [];
    this.programQuery = '';
    this.classQuery = '';
    this.classList = [];
    this.filteredClassList = [];
    this.academicYearQuery = '';
    this.academicYearList = [];
    this.filteredAcademicYearList = [];
    this.stateQuery = '';
    this.filteredStateList = [...this.stateList];
    this.testTypeQuery = '';
    this.filteredTestTypeList = [...this.testTypeList];
    this.applySubjects([]);
    setTimeout(() => this.initDatePickers(), 0);
  }

  /** Normalize createTest error HTTP body. */
  private getCreateTestErrorBody(source: any): any {
    return source?.error != null && typeof source.error === 'object'
      ? source.error
      : source;
  }

  /** Read createTest API error body (422 / duplicate test). */
  private getCreateTestErrorMessage(source: any): string {
    const body = this.getCreateTestErrorBody(source);

    const desc = String(
      body?.error_description ?? body?.message ?? body?.errorMessage ?? ''
    ).trim();
    if (desc) {
      return desc;
    }
    if (typeof body === 'string' && body.trim() && body !== 'error') {
      return body.trim();
    }
    if (source?.message && !String(source.message).includes('Http failure response')) {
      return String(source.message).trim();
    }
    return 'Failed to create test';
  }

  /** Store message + existing test code/name from API response. */
  private applyCreateTestError(source: any): void {
    const body = this.getCreateTestErrorBody(source);
    this.createTestError = this.getCreateTestErrorMessage(source);

    this.existingTestCode = String(
      body?.testCode ?? body?.existingTestCode ?? body?.test_code ??
      body?.existingTest?.testCode ?? ''
    ).trim();

    this.existingTestName = String(
      body?.testName ?? body?.existingTestName ?? body?.existingTest?.testName ?? ''
    ).trim();

    if (!this.existingTestName) {
      const nameMatch = this.createTestError.match(/Existing Test:\s*(.+)$/i);
      this.existingTestName = nameMatch?.[1]?.trim() || '';
    }

    if (!this.existingTestCode) {
      const codeMatch =
        this.createTestError.match(/Test Code:\s*(\d+)/i) ||
        this.createTestError.match(/testCode[=:\s]+(\d+)/i);
      this.existingTestCode = codeMatch?.[1]?.trim() || '';
    }
  }

  private getCreateTestErrorToastMessage(): string {
    const parts = [this.createTestError];
    if (this.existingTestCode) {
      parts.push(`Test Code: ${this.existingTestCode}`);
    }
    return parts.filter(Boolean).join(' | ');
  }

  onCreateTest(): void {
    if (this.isCreating) {
      return;
    }
    this.createTestError = '';
    this.existingTestCode = '';
    this.existingTestName = '';

    // One-by-one: alert + focus only the first missing field
    const missing = this.getFirstMissingStep();
    if (missing) {
      this.showOneByOneError(missing);
      return;
    }

    this.openCreateConfirm();
  }

  closeCreateConfirm(): void {
    this.showCreateConfirm = false;
  }

  private openCreateConfirm(): void {
    const v = this.testForm.value;
    this.confirmRows = [
      { label: 'State', value: String(v.stateName || '-') },
      { label: 'Academic Year', value: String(v.academicYear || '-') },
      { label: 'Class', value: String(v.className || '-') },
      { label: 'Group', value: String(v.groupName || '-') },
      { label: 'Stream', value: String(v.streamName || '-') },
      { label: 'Program', value: String(v.programName || '-') },
      { label: 'Test Type', value: String(v.testType || '-') },
      { label: 'Start Date', value: String(v.testStartDate || '-') },
      { label: 'End Date', value: String(v.testEndDate || '-') },
      { label: 'Total Marks', value: String(this.totalMarks) }
    ];
    this.confirmSubjectLabels = this.getSelectedSubjects().map((s) => {
      const marks = this.testForm.get(s.key)?.value;
      return `${s.label}${marks != null && marks !== '' ? ' (' + marks + ')' : ''}`;
    });
    this.showCreateConfirm = true;
  }

  confirmCreateTest(): void {
    if (this.isCreating) {
      return;
    }
    this.showCreateConfirm = false;
    const payload = this.buildCreatePayload();
    if (!payload.subjects.length) {
      this.showOneByOneError({
        key: this.subjects[0]?.key || 'groupName',
        message: 'Please select at least one subject'
      });
      return;
    }
    this.isCreating = true;
    this.spinner.show();
    this.services.createTest(payload).subscribe({
      next: (res: any) => {
        if (res?.error || res?.error_description) {
          this.applyCreateTestError(res);
          this.toast.showError(this.getCreateTestErrorToastMessage());
          this.isCreating = false;
          this.spinner.hide();
          return;
        }
        this.createTestError = '';
        this.existingTestCode = '';
        this.existingTestName = '';
        this.toast.showSuccess(res?.message || 'Created successfully');
        this.isCreating = false;
        this.spinner.hide();
        this.onReset();
      },
      error: (err) => {
        console.error('Failed to create test', err);
        this.applyCreateTestError(err);
        this.toast.showError(this.getCreateTestErrorToastMessage());
        this.isCreating = false;
        this.spinner.hide();
      }
    });
  }
}
